import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Checkbox } from './components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from './components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './components/ui/dialog';
import { Home, Calculator, Phone, Mail, Euro, Building2, Users, CreditCard, Eye, Settings, Loader2, FileDown, Calendar, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import Logo from './components/Logo';
import loveliaLogoJpg from './assets/lovelia-logo.jpg';
import { t } from './translations';
import AdminPage from './components/AdminPage';
import { AddressAutocomplete } from './components/AddressAutocomplete';
import { GareAutocomplete } from './components/GareAutocomplete';
import { downloadRecapPdf } from './components/pdfGenerator';
import './App.css';

// Utilise la même origine que la page (le backend est servi sur le même domaine via /api).
// Fallback sur REACT_APP_BACKEND_URL côté serveur/SSR uniquement.
const BACKEND_URL = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Composant principal du formulaire d'estimation

const EstimationForm = () => {
  // Force scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // État pour la langue
  const [language, setLanguage] = useState('fr');

  const [formData, setFormData] = useState({
    recommande: '',
    nom_recommandation: '',
    civilite: '',
    nom: '',
    prenom: '',
    adresse: '',
    code_postal: '',
    ville: '',
    telephone: '',
    email: '',
    date_naissance_monsieur: '',
    date_naissance_madame: '',
    situation_familiale: '',
    type_achat: '',
    conjoint_nom: '',
    conjoint_prenom: '',
    conjoint_telephone: '',
    conjoint_email: '',
    nombre_enfants: 0,
    loyer_actuel: 0,
    salaire_brut_monsieur: 0,
    salaire_net_monsieur: 0,
    primes_monsieur: 0,
    statut_monsieur: '',
    profession_monsieur: '',
    lieu_travail_monsieur: '',
    salaire_brut_madame: 0,
    salaire_net_madame: 0,
    primes_madame: 0,
    statut_madame: '',
    profession_madame: '',
    lieu_travail_madame: '',
    revenus_caf: 0,
    revenu_fiscal_reference: 0,
    banque_actuelle: '',
    credit_en_cours: 'non',
    organisme_credit: '',
    mensualite_credit: 0,
    montant_restant_credit: 0,
    apport_projet: 0,
    mensualite_souhaitee: 0,
    type_bien: '',
    nombre_chambres: '',
    ville_choix_1: '',
    ville_choix_2: '',
    ville_choix_3: '',
    criteres: [],
    duree_trajet: '',
    station_emploi: '',
    description_bien: '',
    ville_code_insee: '',
    adresse_code_insee: '',
    adresse_lat: null,
    adresse_lon: null,
    adresse_travail: '',
    travail_lat: null,
    travail_lon: null,
    gare_depart: '',
    gare_depart_lat: null,
    gare_depart_lon: null,
    gare_depart_lignes: null
  });

  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const totalSteps = 4;

  // Attribution/provenance (paramètres d'URL) + dédoublonnage CRM
  const [clientIdHint, setClientIdHint] = useState('');
  const [commercialInfo, setCommercialInfo] = useState(null);
  const DEFAULT_AGENT = { name: 'Teddy FRESILLON', whatsapp: '33647521453' };
  // Coordonnées affichées dans le footer selon le commercial attribué (lien ?c=code)
  const CONTACTS = {
    teddy: { name: 'Teddy FRESILLON', email: 'teddy.fresillon@lovelia-immo.fr', phone: '06.47.52.14.53' },
    durmus: { name: 'Durmus GULESER', email: 'durmus.guleser@lovelia-immo.fr', phone: '06 99 03 54 25' },
  };
  const DEFAULT_CONTACT = CONTACTS.teddy;
  const [repContact, setRepContact] = useState(DEFAULT_CONTACT);
  const attribution = React.useRef({ src: '', commercial: '', commercial_id: '', commercial_code: '', apporteur: '', utm_source: '', utm_medium: '', utm_campaign: '', utm_content: '', utm_term: '' });

  // Lookup CRM par téléphone : pré-remplit silencieusement si le prospect est connu
  const lookupByPhone = async (phone) => {
    try {
      const res = await axios.get(`${API}/crm/prospect/lookup-by-phone`, { params: { phone }, timeout: 15000 });
      const data = res.data || {};
      if (data.found && data.client) {
        const c = data.client;
        setClientIdHint(data.client_id || c.id || '');
        setFormData(prev => ({
          ...prev,
          civilite: prev.civilite || c.civilite || '',
          prenom: prev.prenom || c.prenom || '',
          nom: prev.nom || c.nom || '',
          email: prev.email || c.email || '',
        }));
        if (c.prenom) toast.success(`Bienvenue ${c.prenom}, vérifiez vos informations 👋`);
      }
    } catch (e) { /* silencieux : le CRM dédoublonnera en aval */ }
  };

  // Capture des paramètres d'URL au chargement (phone, src, utm_*) + lookup
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const get = (k) => (params.get(k) || '').trim();
      let saved = {};
      try { saved = JSON.parse(sessionStorage.getItem('hp_attribution') || '{}'); } catch (e) {}
      const phone = get('phone') || saved.phone || '';
      const src = get('src') || saved.src || '';
      // Attribution commercial : code du lien (?c=) ou email/identifiant (transition)
      const commercial = get('commercial') || get('commercial_email') || get('owner_email') || get('c') || saved.commercial || '';
      // Apporteur (Phase 2) : capté et transmis, traité comme générique pour l'instant
      const apporteur = get('a') || get('apporteur') || saved.apporteur || '';
      const utm = {
        utm_source: get('utm_source') || saved.utm_source || '',
        utm_medium: get('utm_medium') || saved.utm_medium || '',
        utm_campaign: get('utm_campaign') || saved.utm_campaign || '',
        utm_content: get('utm_content') || saved.utm_content || '',
        utm_term: get('utm_term') || saved.utm_term || '',
      };
      attribution.current = { src, commercial, commercial_id: '', commercial_code: commercial, apporteur, apporteur_id: '', ...utm };
      sessionStorage.setItem('hp_attribution', JSON.stringify({ phone, src, commercial, apporteur, ...utm }));
      // Coordonnées du footer selon le code commercial (défaut : Teddy)
      setRepContact(CONTACTS[(commercial || '').toLowerCase()] || DEFAULT_CONTACT);
      // Résolution du contexte questionnaire (identité commercial + apporteur) via le CRM.
      // Si ?a= et ?c= présents, l'apporteur gagne (rattachement côté CRM).
      // Repli automatique sur /api/commercials si l'endpoint CRM est indisponible.
      axios.get(`${API}/public/questionnaire-context`, { params: { c: commercial, a: apporteur } })
        .then((r) => {
          const d = r.data || {};
          if (!d.found) return;
          setCommercialInfo(d);
          attribution.current.commercial_id = d.commercial_id || '';
          attribution.current.commercial_code = d.code || commercial || '';
          attribution.current.apporteur = d.apporteur_code || apporteur || '';
          attribution.current.apporteur_id = d.apporteur_id || '';
          // Si le commercial n'est pas dans la table locale, prendre nom/téléphone du CRM
          if (!CONTACTS[(commercial || '').toLowerCase()] && (d.name || d.phone)) {
            setRepContact({ name: d.name || DEFAULT_CONTACT.name, email: DEFAULT_CONTACT.email, phone: d.phone || DEFAULT_CONTACT.phone });
          }
        })
        .catch(() => {});
      // Ping d'ouverture (une fois par session) pour le taux de conversion par canal
      if (!sessionStorage.getItem('hp_open_tracked')) {
        let sid = sessionStorage.getItem('hp_session_id');
        if (!sid) {
          sid = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now());
          sessionStorage.setItem('hp_session_id', sid);
        }
        axios.post(`${API}/track/open`, { session_id: sid, src, ...utm }).catch(() => {});
        sessionStorage.setItem('hp_open_tracked', '1');
      }
      if (phone) {
        setFormData(prev => ({ ...prev, telephone: prev.telephone || phone }));
        lookupByPhone(phone);
      }
    } catch (e) {}
  }, []);

  // Scroll en haut de page à chaque changement d'étape
  React.useEffect(() => {
    window.scrollTo(0, 0);
    setHasAttemptedSubmit(false); // Réinitialiser quand on change d'étape
  }, [currentStep]);

  // (a) Restauration du brouillon depuis le navigateur au chargement
  const DRAFT_KEY = 'hp_questionnaire_draft';
  const draftLoaded = React.useRef(false);
  React.useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.formData) setFormData(prev => ({ ...prev, ...parsed.formData }));
        if (parsed.currentStep) setCurrentStep(parsed.currentStep);
        toast.success(t('draftRestored', language));
        setShowDraftBanner(true);
      }
    } catch (e) {}
    setTimeout(() => { draftLoaded.current = true; }, 0);
  }, []);

  // Recommencer à zéro : efface le brouillon et recharge une page vierge
  const discardDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch (e) {}
    window.location.reload();
  };

  // (a) Sauvegarde automatique du brouillon à chaque modification (après restauration)
  React.useEffect(() => {
    if (!draftLoaded.current) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ formData, currentStep }));
    } catch (e) {}
  }, [formData, currentStep]);

  // (c) Validation temps réel email & téléphone
  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
  const isValidPhone = (v) => /^(\+\d{9,15}|0\d{9})$/.test((v || '').replace(/[^\d+]/g, ''));

  const CRITERES_OPTIONS = [
    'Transport à proximité',
    'Écoles à proximité',
    'Commerces à proximité',
    'Centre ville',
    'Calme (éloigné du centre ville)',
  ];

  const toggleCritere = (value) => {
    setFormData(prev => {
      const list = prev.criteres || [];
      const has = list.includes(value);
      return { ...prev, criteres: has ? list.filter(c => c !== value) : [...list, value] };
    });
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNumericFocus = (name) => {
    // Effacer le 0 par défaut quand on clique sur un champ numérique
    if (formData[name] === 0) {
      setFormData(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleNumericBlur = (name, value) => {
    // Remettre 0 si le champ est vide
    const numericValue = value === '' ? 0 : parseFloat(value) || 0;
    setFormData(prev => ({
      ...prev,
      [name]: numericValue
    }));
  };

  const generateWhatsAppMessage = (data) => {
    const totalRevenus = (
      data.salaire_brut_monsieur + data.salaire_net_monsieur +
      data.salaire_brut_madame + data.salaire_net_madame +
      data.revenus_caf
    );
    
    const message = `🏠 NOUVELLE DEMANDE D'ESTIMATION IMMOBILIÈRE

📋 CLIENT:
• Nom: ${data.nom} ${data.prenom}
• Téléphone: ${data.telephone}
• Email: ${data.email}
• Adresse: ${data.adresse}

👨‍👩‍👧‍👦 SITUATION:
• Situation familiale: ${data.situation_familiale}
• Nombre d'enfants: ${data.nombre_enfants}
• Loyer actuel: ${data.loyer_actuel.toLocaleString()}€

💰 REVENUS MENSUELS:
• Monsieur: ${data.salaire_net_monsieur.toLocaleString()}€ net (${data.statut_monsieur || 'Non précisé'})
• Madame: ${data.salaire_net_madame.toLocaleString()}€ net (${data.statut_madame || 'Non précisé'})
• CAF: ${data.revenus_caf.toLocaleString()}€
• TOTAL: ${totalRevenus.toLocaleString()}€/mois

🏦 SITUATION FINANCIÈRE:
• Banque: ${data.banque_actuelle}
• Crédits: ${data.credit_en_cours}
• Apport: ${data.apport_projet.toLocaleString()}€
• Mensualité souhaitée: ${data.mensualite_souhaitee.toLocaleString()}€

🏡 PROJET IMMOBILIER:
• Type: ${data.type_bien}
• Pièces: ${data.nombre_chambres}
• Villes: ${[data.ville_choix_1, data.ville_choix_2, data.ville_choix_3].filter(Boolean).join(', ')}
• Critères: ${(data.criteres || []).join(', ')}
• Trajet max travail: ${data.duree_trajet || '-'}${data.station_emploi ? ' (' + data.station_emploi + ')' : ''}
• Autres précisions: ${data.description_bien || '-'}

📅 Demande du ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`;

    return encodeURIComponent(message);
  };

  const openWhatsApp = () => {
    if (submittedData) {
      const message = generateWhatsAppMessage(submittedData);
      const wa = (commercialInfo && commercialInfo.whatsapp) || DEFAULT_AGENT.whatsapp;
      const whatsappUrl = `https://wa.me/${wa}?text=${message}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  // Écran de succès après soumission
  if (submissionSuccess && submittedData) {
    const agentName = (commercialInfo && commercialInfo.name) || DEFAULT_AGENT.name;
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <Logo size="large" className="mx-auto mb-8" />
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="border-[#DFCE9F] bg-[#FAF6EE] shadow-xl">
              <CardHeader className="text-center">
                <div className="flex items-center justify-center mb-4">
                  <div className="h-16 w-16 bg-gradient-to-br from-[#CCAE77] to-[#B09259] rounded-full flex items-center justify-center shadow-md ring-4 ring-[#F3EAD8]">
                    <svg className="h-8 w-8 text-[#0E1E34]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <CardTitle className="text-2xl text-[#0E1E34]">Demande envoyée avec succès !</CardTitle>
                <CardDescription className="text-[#142843] mt-2">
                  Votre demande a été transmise à {agentName}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 text-center">
                <div className="bg-white p-5 rounded-xl border border-[#DFCE9F]">
                  <h3 className="font-semibold text-[#0E1E34] mb-2">Résumé de votre demande</h3>
                  <div className="text-sm text-[#142843] space-y-1">
                    <p><strong className="text-[#0E1E34]">Client :</strong> {submittedData.prenom} {submittedData.nom}</p>
                    <p><strong className="text-[#0E1E34]">Contact :</strong> {submittedData.telephone}</p>
                    <p><strong className="text-[#0E1E34]">Projet :</strong> {submittedData.type_bien === 'appartement' ? 'Appartement' : submittedData.type_bien === 'maison' ? 'Maison' : submittedData.type_bien}{submittedData.nombre_chambres ? ` - ${submittedData.nombre_chambres === '1' ? '1 pièce' : submittedData.nombre_chambres === '5+' ? '5 pièces ou +' : submittedData.nombre_chambres + ' pièces'}` : ''}</p>
                    <p><strong className="text-[#0E1E34]">Villes :</strong> {[submittedData.ville_choix_1, submittedData.ville_choix_2, submittedData.ville_choix_3].filter(Boolean).join(', ')}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="text-[#142843]">
                    📧 Un email détaillé a été envoyé à {agentName}<br/>
                    📱 Vous pouvez également lui envoyer un message WhatsApp
                  </p>
                  
                  <Button
                    onClick={openWhatsApp}
                    className="bg-gradient-to-r from-[#CCAE77] to-[#B09259] hover:from-[#B09259] hover:to-[#8F7344] text-[#0E1E34] font-semibold px-8 py-3 text-lg shadow-md"
                    size="lg"
                  >
                    <Phone className="h-5 w-5 mr-2" />
                    Envoyer sur WhatsApp
                  </Button>

                  {commercialInfo && commercialInfo.booking_url && (
                    <div>
                      <Button
                        onClick={() => window.open(commercialInfo.booking_url, '_blank')}
                        className="bg-[#0E1E34] hover:bg-[#142843] text-white px-8 py-3 text-lg"
                        size="lg"
                        data-testid="booking-button"
                      >
                        <Calendar className="h-5 w-5 mr-2" />
                        Prendre rendez-vous avec {agentName}
                      </Button>
                    </div>
                  )}

                  <div>
                    <Button
                      onClick={handleDownloadPdf}
                      disabled={pdfLoading}
                      variant="outline"
                      className="border-[#DFCE9F] text-[#8F7344] hover:bg-[#FAF6EE] px-6 py-3"
                      size="lg"
                      data-testid="download-pdf-button"
                    >
                      {pdfLoading ? (
                        <span className="flex items-center"><Loader2 className="h-5 w-5 mr-2 animate-spin" />{t('pdfGenerating', language)}</span>
                      ) : (
                        <span className="flex items-center"><FileDown className="h-5 w-5 mr-2" />{t('pdfButton', language)}</span>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-[#4C6388]">
                    Le message sera automatiquement pré-rempli avec vos informations
                  </p>

                  <button
                    type="button"
                    onClick={startNewRequest}
                    className="text-sm text-[#7E92AE] hover:text-[#142843] underline transition-colors"
                    data-testid="new-request-button"
                  >
                    {t('newRequest', language)}
                  </button>
                </div>

                <div className="border-t border-[#DFCE9F] pt-4">
                  <p className="text-sm text-[#142843]">
                    <strong className="text-[#0E1E34]">Prochaines étapes :</strong><br/>
                    {agentName} vous contactera sous 24h pour analyser votre dossier<br/>
                    et vous proposer des biens correspondant à vos critères.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Toaster richColors position="top-right" />
      </div>
    );
  }

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.recommande && formData.civilite && formData.nom && formData.prenom && formData.adresse && formData.code_postal && formData.ville && isValidPhone(formData.telephone) && isValidEmail(formData.email);
      case 2:
        // Statut requis pour chaque personne affichée ; salaire individuel peut être 0
        // (ex. conjoint sans emploi) tant que le foyer déclare au moins un revenu net.
        let requiredFields = formData.situation_familiale;
        if (shouldShowMonsieurFields()) {
          requiredFields = requiredFields && formData.statut_monsieur;
        }
        if (shouldShowMadameFields()) {
          requiredFields = requiredFields && formData.statut_madame;
        }
        if (shouldShowMonsieurFields() || shouldShowMadameFields()) {
          requiredFields = requiredFields && householdHasIncome();
        }
        return requiredFields;
      case 3:
        // Validation pour l'étape 3 - apport_projet n'est plus obligatoire
        const basicFields = formData.credit_en_cours;
        if (formData.credit_en_cours === 'oui') {
          return basicFields && formData.organisme_credit && formData.mensualite_credit > 0;
        }
        return basicFields;
      case 4:
        return formData.type_bien && formData.nombre_chambres && formData.ville_choix_1;
      default:
        return true;
    }
  };

  // Logique pour déterminer quels champs afficher selon civilité et situation
  const shouldShowMonsieurFields = () => {
    const situationCouple = ['marie', 'marie_separation', 'pacs', 'concubinage'].includes(formData.situation_familiale);
    const situationSeul = ['celibataire', 'divorce', 'veuf'].includes(formData.situation_familiale);
    
    if (situationCouple) return true; // Toujours afficher pour les couples
    if (situationSeul && formData.civilite === 'Monsieur') return true;
    return false;
  };

  const shouldShowMadameFields = () => {
    const situationCouple = ['marie', 'marie_separation', 'pacs', 'concubinage'].includes(formData.situation_familiale);
    const situationSeul = ['celibataire', 'divorce', 'veuf'].includes(formData.situation_familiale);
    
    if (situationCouple) return true; // Toujours afficher pour les couples
    if (situationSeul && formData.civilite === 'Madame') return true;
    return false;
  };

  // Au moins un revenu net déclaré dans le foyer (permet 0€ pour un conjoint qui ne travaille pas)
  const householdHasIncome = () =>
    (Number(formData.salaire_net_monsieur) || 0) > 0 || (Number(formData.salaire_net_madame) || 0) > 0;

  const saveLeadProgress = (step) => {
    try {
      if (!isValidEmail(formData.email)) return;
      let sid = sessionStorage.getItem('hp_session_id');
      if (!sid) {
        sid = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now());
        sessionStorage.setItem('hp_session_id', sid);
      }
      axios.post(`${API}/lead/progress`, {
        session_id: sid,
        prenom: formData.prenom,
        nom: formData.nom,
        email: formData.email,
        telephone: formData.telephone,
        src: attribution.current.src || '',
        commercial: attribution.current.commercial || '',
        commercial_id: attribution.current.commercial_id || '',
        commercial_code: attribution.current.commercial_code || attribution.current.commercial || '',
        apporteur_code: attribution.current.apporteur || '',
        apporteur_id: attribution.current.apporteur_id || '',
        utm_source: attribution.current.utm_source || '',
        utm_medium: attribution.current.utm_medium || '',
        utm_campaign: attribution.current.utm_campaign || '',
        utm_content: attribution.current.utm_content || '',
        utm_term: attribution.current.utm_term || '',
        origin: window.location.origin,
        step,
      }).catch(() => {});
    } catch (e) {}
  };

  const regenerateSession = () => {
    const sid = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now()) + Math.random();
    sessionStorage.setItem('hp_session_id', sid);
    return sid;
  };

  const resetForFriend = () => {
    // Partage de lien : on repart totalement à zéro pour le proche (nouveau session_id)
    try {
      ['hp_session_id', 'hp_attribution', 'hp_open_tracked', 'hp_bound_email', 'hp_bound_phone', DRAFT_KEY].forEach((k) => {
        sessionStorage.removeItem(k);
        localStorage.removeItem(k);
      });
    } catch (e) {}
    window.location.href = window.location.origin + window.location.pathname;
  };

  const advanceStep = () => {
    saveLeadProgress(currentStep + 1);
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const nextStep = () => {
    if (!validateStep(currentStep)) {
      setHasAttemptedSubmit(true);
      toast.error(t('fillRequired', language));
      scrollToFirstError(currentStep);
      return;
    }
    if (currentStep === 1) {
      const em = (formData.email || '').trim().toLowerCase();
      const ph = (formData.telephone || '').trim();
      const prevEm = sessionStorage.getItem('hp_bound_email') || '';
      const prevPh = sessionStorage.getItem('hp_bound_phone') || '';
      // Détection de partage de lien : un email différent a déjà été vu dans cette session
      if (prevEm && em && prevEm !== em) {
        setShowShareDialog(true);
        return;
      }
      // Régénère le session_id si l'identité (email/tel) change — analytics uniquement
      if ((prevEm && em && prevEm !== em) || (prevPh && ph && prevPh !== ph)) {
        regenerateSession();
      }
      sessionStorage.setItem('hp_bound_email', em);
      sessionStorage.setItem('hp_bound_phone', ph);
    }
    advanceStep();
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Champs obligatoires par étape (ordre d'affichage)
  const requiredOrder = {
    1: ['recommande', 'civilite', 'nom', 'prenom', 'adresse', 'code_postal', 'ville', 'telephone', 'email'],
    2: ['situation_familiale', 'salaire_net_monsieur', 'statut_monsieur', 'salaire_net_madame', 'statut_madame'],
    3: ['organisme_credit', 'mensualite_credit'],
    4: ['type_bien', 'nombre_chambres', 'ville_choix_1'],
  };

  // Vérifie si un champ obligatoire est vide (sans tenir compte de la tentative)
  const isMissing = (name) => {
    switch (name) {
      case 'recommande': return !formData.recommande;
      case 'civilite': return !formData.civilite;
      case 'nom': return !formData.nom;
      case 'prenom': return !formData.prenom;
      case 'adresse': return !formData.adresse;
      case 'code_postal': return !formData.code_postal;
      case 'ville': return !formData.ville;
      case 'telephone': return !isValidPhone(formData.telephone);
      case 'email': return !isValidEmail(formData.email);
      case 'situation_familiale': return !formData.situation_familiale;
      case 'salaire_net_monsieur': return shouldShowMonsieurFields() && !householdHasIncome();
      case 'statut_monsieur': return shouldShowMonsieurFields() && !formData.statut_monsieur;
      case 'salaire_net_madame': return shouldShowMadameFields() && !householdHasIncome();
      case 'statut_madame': return shouldShowMadameFields() && !formData.statut_madame;
      case 'organisme_credit': return formData.credit_en_cours === 'oui' && !formData.organisme_credit;
      case 'mensualite_credit': return formData.credit_en_cours === 'oui' && !(formData.mensualite_credit > 0);
      case 'type_bien': return !formData.type_bien;
      case 'nombre_chambres': return !formData.nombre_chambres;
      case 'ville_choix_1': return !formData.ville_choix_1;
      default: return false;
    }
  };

  // Champ obligatoire manquant affiché en rouge (après tentative de validation)
  const isInvalid = (name) => hasAttemptedSubmit && isMissing(name);

  // Classe CSS de surbrillance rouge pour un champ obligatoire manquant
  const errCls = (name) => isInvalid(name) ? ' border-[#C2A36B] ring-2 ring-[#C2A36B]/40 bg-[#FAF6EE]' : '';

  // Nombre de champs obligatoires restants pour une étape
  const remainingCount = (step) => (requiredOrder[step] || []).filter((f) => isMissing(f)).length;

  // Défile et focalise le premier champ obligatoire manquant de l'étape
  const scrollToFirstError = (step) => {
    const selectors = {
      recommande: "[data-testid='select-recommande']",
      civilite: "[data-testid='select-civilite']",
      situation_familiale: "[data-testid='select-situation-familiale']",
      type_bien: "[data-testid='select-type-bien']",
      nombre_chambres: "[data-testid='select-nombre-chambres']",
      salaire_net_monsieur: "[data-testid='input-salaire-net-monsieur']",
      salaire_net_madame: "[data-testid='input-salaire-net-madame']",
      statut_monsieur: "[data-testid='radio-statut-monsieur']",
      statut_madame: "[data-testid='radio-statut-madame']",
    };
    const first = (requiredOrder[step] || []).find((f) => isMissing(f));
    if (!first) return;
    const sel = selectors[first] || `#${first}`;
    setTimeout(() => {
      const el = document.querySelector(sel);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        try { el.focus({ preventScroll: true }); } catch (e) {}
      }
    }, 100);
  };

  // Fonction pour nettoyer les valeurs null/undefined
  const cleanFormData = (data) => {
    const cleaned = { ...data };
    Object.keys(cleaned).forEach(key => {
      if (cleaned[key] === null || cleaned[key] === undefined) {
        cleaned[key] = '';
      }
    });
    return cleaned;
  };

  // Récapitulatif des réponses (champs renseignés uniquement)
  function buildRecap(src = formData) {
    const situationLabels = {
      celibataire: 'Célibataire', marie: 'Marié(e)', marie_separation: 'Marié(e) en séparation de biens', pacs: 'Pacsé(e)',
      concubinage: 'Concubinage', divorce: 'Divorcé(e)', veuf: 'Veuf(ve)',
    };
    const typeAchatLabels = { seul: 'Seul', deux: 'À deux', sci: 'En SCI' };
    const conjointLabel = [src.conjoint_prenom, src.conjoint_nom].filter(Boolean).join(' ')
      + ([src.conjoint_telephone, src.conjoint_email].filter(Boolean).length
        ? ` (${[src.conjoint_telephone, src.conjoint_email].filter(Boolean).join(' · ')})` : '');
    const typeBienLabel = src.type_bien === 'appartement' ? t('apartment', language)
      : src.type_bien === 'maison' ? t('house', language) : src.type_bien;
    const piecesLabel = src.nombre_chambres === '1' ? '1 pièce'
      : src.nombre_chambres === '5+' ? '5 pièces ou +'
      : src.nombre_chambres ? `${src.nombre_chambres} pièces` : '';
    const creditLabel = src.credit_en_cours === 'oui' ? t('yes', language)
      : src.credit_en_cours === 'non' ? t('no', language) : src.credit_en_cours;
    const criteresLabel = Array.isArray(src.criteres) ? src.criteres.join(', ') : (src.criteres || '');
    const rows = [
      [t('civility', language), src.civilite],
      [t('lastName', language), src.nom],
      [t('firstName', language), src.prenom],
      [t('address', language), src.adresse],
      [t('postalCode', language), src.code_postal],
      [t('city', language), src.ville],
      [t('phone', language), src.telephone],
      [t('email', language), src.email],
      [t('maritalStatus', language), situationLabels[src.situation_familiale] || src.situation_familiale],
      ['Vous souhaitez acheter', typeAchatLabels[src.type_achat] || src.type_achat],
      ['Conjoint', conjointLabel],
      ["Nombre d'enfants", src.nombre_enfants],
      [`${t('netSalary', language)} Monsieur`, src.salaire_net_monsieur ? `${src.salaire_net_monsieur} €` : ''],
      [`${t('netSalary', language)} Madame`, src.salaire_net_madame ? `${src.salaire_net_madame} €` : ''],
      ['Revenu fiscal de référence (N-1)', src.revenu_fiscal_reference ? `${src.revenu_fiscal_reference} €` : ''],
      [t('existingCreditQuestion', language), creditLabel],
      ['Apport disponible', src.apport_projet ? `${src.apport_projet} €` : ''],
      ['Mensualité souhaitée', src.mensualite_souhaitee ? `${src.mensualite_souhaitee} €` : ''],
      [t('propertyTypeLabel', language), typeBienLabel],
      ['Nombre de pièces', piecesLabel],
      ['Ville/Département (choix 1)', src.ville_choix_1],
      ['Ville/Département (choix 2)', src.ville_choix_2],
      ['Ville/Département (choix 3)', src.ville_choix_3],
      ['Critères importants', criteresLabel],
      ['Durée max trajet travail', src.duree_trajet],
      ['Station emploi', src.station_emploi],
      ['Autres précisions', src.description_bien],
    ];
    return rows.filter(([, v]) => v !== '' && v !== 0 && v !== null && v !== undefined);
  };

  // Télécharge le récapitulatif PDF (à partir des données soumises)
  async function handleDownloadPdf() {
    try {
      setPdfLoading(true);
      const src = submittedData || formData;
      const rows = buildRecap(src);
      await downloadRecapPdf(rows, {
        logoUrl: loveliaLogoJpg,
        title: t('pdfTitle', language),
        intro: t('pdfIntro', language),
        colField: t('pdfField', language),
        colValue: t('pdfValue', language),
        footer: t('pdfFooter', language),
        filename: `recap_lovelia_${(src.nom || 'client').toString().replace(/[^a-zA-Z0-9]/g, '_')}.pdf`,
      });
    } catch (e) {
      toast.error(t('pdfError', language));
    } finally {
      setPdfLoading(false);
    }
  };

  function startNewRequest() {
    setSubmissionSuccess(false);
    setSubmittedData(null);
    setCurrentStep(1);
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateStep(4)) {
      setHasAttemptedSubmit(true);
      toast.error(t('fillRequired', language));
      scrollToFirstError(4);
      return;
    }

    // Ouvrir le récapitulatif avant l'envoi définitif
    setShowRecap(true);
  };

  const confirmAndSubmit = async () => {
    // ANTI-VIDAGE : ne jamais envoyer une identité incomplète
    if (!(formData.nom || '').trim() || !(formData.prenom || '').trim() || !(formData.telephone || '').trim()) {
      toast.error('Nom, prénom et téléphone sont obligatoires.');
      return;
    }
    setShowRecap(false);
    setIsLoading(true);
    
    try {
      // Nettoyer les données avant l'envoi
      const cleanedData = cleanFormData(formData);

      // Critères en champ dédié (chaîne séparée par des virgules)
      cleanedData.criteres = (formData.criteres || []).join(', ');
      // duree_trajet, station_emploi et description_bien sont déjà des champs dédiés

      // Attribution/provenance + hint dédoublonnage (capturés à l'ouverture)
      cleanedData.src = attribution.current.src || '';
      cleanedData.commercial = attribution.current.commercial || '';
      cleanedData.commercial_id = attribution.current.commercial_id || '';
      cleanedData.commercial_code = attribution.current.commercial_code || attribution.current.commercial || '';
      cleanedData.apporteur_code = attribution.current.apporteur || '';
      cleanedData.apporteur_id = attribution.current.apporteur_id || '';
      cleanedData.utm_source = attribution.current.utm_source || '';
      cleanedData.utm_medium = attribution.current.utm_medium || '';
      cleanedData.utm_campaign = attribution.current.utm_campaign || '';
      cleanedData.utm_content = attribution.current.utm_content || '';
      cleanedData.utm_term = attribution.current.utm_term || '';
      if (clientIdHint) cleanedData.client_id_hint = clientIdHint;
      cleanedData.session_id = sessionStorage.getItem('hp_session_id') || '';
      // Firewall actif : type de soumission (pas de magic link authentifié => toujours nouveau client)
      cleanedData.submission_type = 'new_client';
      cleanedData.referrer = document.referrer || 'direct';
      // Langue du prospect (email de confirmation multilingue)
      cleanedData.language = language;

      const response = await axios.post(`${API}/estimation`, cleanedData, { timeout: 30000 });

      // Réponses de garde (anti-doublon / identité incomplète)
      if (response.data && response.data.success === false) {
        setIsLoading(false);
        toast.error(response.data.message || "Envoi refusé.");
        return;
      }

      if (response.status === 200) {
        toast.success('Votre demande d\'estimation a été envoyée avec succès ! 🏠');
        toast.success('Vous recevrez une réponse sous 24h par email et WhatsApp');
        
        // Stocker les données pour WhatsApp et afficher le succès
        setSubmittedData(formData);
        setSubmissionSuccess(true);
        try { localStorage.removeItem(DRAFT_KEY); } catch (e) {}
        
        // Réinitialise le formulaire en arrière-plan (l'écran de succès reste affiché
        // tant que l'utilisateur ne clique pas sur « Nouvelle demande »)
        setFormData({
            recommande: '',
            nom_recommandation: '',
            civilite: '',
            nom: '',
            prenom: '',
            adresse: '',
            code_postal: '',
            ville: '',
            telephone: '',
            email: '',
            date_naissance_monsieur: '',
            date_naissance_madame: '',
            situation_familiale: '',
            type_achat: '',
            conjoint_nom: '',
            conjoint_prenom: '',
            conjoint_telephone: '',
            conjoint_email: '',
            nombre_enfants: 0,
            loyer_actuel: 0,
            salaire_brut_monsieur: 0,
            salaire_net_monsieur: 0,
            primes_monsieur: 0,
            statut_monsieur: '',
            profession_monsieur: '',
            lieu_travail_monsieur: '',
            salaire_brut_madame: 0,
            salaire_net_madame: 0,
            primes_madame: 0,
            statut_madame: '',
            profession_madame: '',
            lieu_travail_madame: '',
            revenus_caf: 0,
            revenu_fiscal_reference: 0,
            banque_actuelle: '',
            credit_en_cours: 'non',
            organisme_credit: '',
            mensualite_credit: 0,
            montant_restant_credit: 0,
            apport_projet: 0,
            mensualite_souhaitee: 0,
            type_bien: '',
            nombre_chambres: '',
            ville_choix_1: '',
            ville_choix_2: '',
            ville_choix_3: '',
            villes_souhaitees: '',
            criteres: [],
            duree_trajet: '',
            station_emploi: '',
            description_bien: '',
            ville_code_insee: '',
            adresse_code_insee: '',
            adresse_lat: null,
            adresse_lon: null,
            adresse_travail: '',
            travail_lat: null,
            travail_lon: null,
            gare_depart: '',
            gare_depart_lat: null,
            gare_depart_lon: null,
            gare_depart_lignes: null
        });
        setCurrentStep(1);
      }
    } catch (error) {
      // LOG TEMPORAIRE - erreur détaillée
      console.error('❌ Erreur lors de la soumission:', error);
      console.error('📝 Détails erreur:', error.response?.data);
      console.error('🔢 Status code:', error.response?.status);
      
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Card className="w-full">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-[#8F7344] mr-2" />
                <CardTitle className="text-2xl text-slate-800">{t('personalInfo', language)}</CardTitle>
              </div>
              <CardDescription>{t('personalInfoDesc', language)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="recommande" className="text-slate-700 font-medium">{t('recommended', language)} *</Label>
                <Select onValueChange={(value) => handleInputChange('recommande', value)}>
                  <SelectTrigger data-testid="select-recommande" className={`border-slate-300 focus:border-[#C2A36B]${errCls('recommande')}`}>
                    <SelectValue placeholder={t('selectAnswer', language)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non">{t('no', language)}</SelectItem>
                    <SelectItem value="oui">{t('yes', language)}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.recommande === 'oui' && (
                <div className="space-y-2 bg-[#EEF1F6] p-4 rounded-lg border border-[#AEBCCF]">
                  <Label htmlFor="nom_recommandation" className="text-slate-700 font-medium">{t('recommendedByFull', language)} *</Label>
                  <Input
                    id="nom_recommandation"
                    value={formData.nom_recommandation}
                    onChange={(e) => handleInputChange('nom_recommandation', e.target.value)}
                    className="border-slate-300 focus:border-[#C2A36B]"
                    placeholder={t('recommendedByFull', language)}
                    required
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="civilite" className="text-slate-700 font-medium">{t('civility', language)} *</Label>
                <Select onValueChange={(value) => handleInputChange('civilite', value)}>
                  <SelectTrigger data-testid="select-civilite" className={`border-slate-300 focus:border-[#C2A36B]${errCls('civilite')}`}>
                    <SelectValue placeholder={t('selectCivility', language)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monsieur">{t('mr', language)}</SelectItem>
                    <SelectItem value="Madame">{t('mrs', language)}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nom" className="text-slate-700 font-medium">{t('lastName', language)} *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => handleInputChange('nom', e.target.value)}
                    className={`border-slate-300 focus:border-[#2A456B]${errCls('nom')}`}
                    placeholder={t('lastNamePlaceholder', language)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prenom" className="text-slate-700 font-medium">{t('firstName', language)} *</Label>
                  <Input
                    id="prenom"
                    value={formData.prenom}
                    onChange={(e) => handleInputChange('prenom', e.target.value)}
                    className={`border-slate-300 focus:border-[#2A456B]${errCls('prenom')}`}
                    placeholder={t('firstNamePlaceholder', language)}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="adresse" className="text-slate-700 font-medium">{t('address', language)} *</Label>
                <AddressAutocomplete
                  id="adresse"
                  value={formData.adresse}
                  onChange={(v) => handleInputChange('adresse', v)}
                  onSelect={(p, f) => setFormData(prev => ({
                    ...prev,
                    adresse: p.name || p.label || prev.adresse,
                    code_postal: p.postcode || prev.code_postal,
                    ville: p.city || prev.ville,
                    adresse_code_insee: p.citycode || '',
                    ville_code_insee: p.citycode || prev.ville_code_insee || '',
                    adresse_lat: f?.geometry?.coordinates ? f.geometry.coordinates[1] : prev.adresse_lat,
                    adresse_lon: f?.geometry?.coordinates ? f.geometry.coordinates[0] : prev.adresse_lon,
                  }))}
                  className={`border-slate-300 focus:border-[#2A456B]${errCls('adresse')}`}
                  placeholder={t('addressPlaceholder', language)}
                  data-testid="adresse-input"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code_postal" className="text-slate-700 font-medium">{t('postalCode', language)} *</Label>
                  <Input
                    id="code_postal"
                    value={formData.code_postal}
                    onChange={(e) => handleInputChange('code_postal', e.target.value)}
                    className={`border-slate-300 focus:border-[#2A456B]${errCls('code_postal')}`}
                    placeholder={t('postalCodePlaceholder', language)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ville" className="text-slate-700 font-medium">{t('city', language)} *</Label>
                  <AddressAutocomplete
                    id="ville"
                    value={formData.ville}
                    onChange={(v) => handleInputChange('ville', v)}
                    onSelect={(p) => setFormData(prev => ({
                      ...prev,
                      ville: p.name || p.city || p.label,
                      code_postal: p.postcode || prev.code_postal,
                      ville_code_insee: p.citycode || prev.ville_code_insee || '',
                    }))}
                    type="municipality"
                    className={`border-slate-300 focus:border-[#2A456B]${errCls('ville')}`}
                    placeholder={t('cityPlaceholder', language)}
                    data-testid="ville-input"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telephone" className="text-slate-700 font-medium flex items-center">
                    <Phone className="h-4 w-4 mr-1" />
                    {t('phone', language)} *
                  </Label>
                  <Input
                    id="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => handleInputChange('telephone', e.target.value)}
                    className={`border-slate-300 focus:border-[#2A456B]${errCls('telephone')}${formData.telephone && !isValidPhone(formData.telephone) ? ' border-[#C2A36B]' : ''}`}
                    placeholder={t('phonePlaceholder', language)}
                    required
                  />
                  {formData.telephone && !isValidPhone(formData.telephone) && (
                    <p data-testid="phone-error" className="text-[#C2A36B] text-xs mt-1">{t('invalidPhone', language)}</p>
                  )}
                  {formData.telephone && isValidPhone(formData.telephone) && (
                    <p data-testid="phone-valid" className="text-[#8F7344] text-xs mt-1">✓</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium flex items-center">
                    <Mail className="h-4 w-4 mr-1" />
                    {t('email', language)} *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`border-slate-300 focus:border-[#2A456B]${errCls('email')}${formData.email && !isValidEmail(formData.email) ? ' border-[#C2A36B]' : ''}`}
                    placeholder={t('emailPlaceholder', language)}
                    required
                  />
                  {formData.email && !isValidEmail(formData.email) && (
                    <p data-testid="email-error" className="text-[#C2A36B] text-xs mt-1">{t('invalidEmail', language)}</p>
                  )}
                  {formData.email && isValidEmail(formData.email) && (
                    <p data-testid="email-valid" className="text-[#8F7344] text-xs mt-1">✓</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_naissance_monsieur" className="text-slate-700 font-medium">{t('birthDateMr', language)}</Label>
                  <Input
                    id="date_naissance_monsieur"
                    type="date"
                    value={formData.date_naissance_monsieur}
                    onChange={(e) => handleInputChange('date_naissance_monsieur', e.target.value)}
                    className="border-slate-300 focus:border-[#2A456B]"
                    placeholder={t('birthDatePlaceholder', language)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_naissance_madame" className="text-slate-700 font-medium">{t('birthDateMrs', language)}</Label>
                  <Input
                    id="date_naissance_madame"
                    type="date"
                    value={formData.date_naissance_madame}
                    onChange={(e) => handleInputChange('date_naissance_madame', e.target.value)}
                    className="border-slate-300 focus:border-[#2A456B]"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card className="w-full">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Home className="h-8 w-8 text-[#8F7344] mr-2" />
                <CardTitle className="text-2xl text-slate-800">{t('familySituationAndIncome', language)}</CardTitle>
              </div>
              <CardDescription>{t('familySituationAndIncomeDesc', language)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-testid="rgpd-notice">
                <svg className="h-5 w-5 text-[#8F7344] shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                <p className="text-xs text-slate-600">{t('rgpdNotice', language)}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="situation_familiale" className="text-slate-700 font-medium">{t('maritalStatus', language)} *</Label>
                  <Select onValueChange={(value) => handleInputChange('situation_familiale', value)}>
                    <SelectTrigger data-testid="select-situation-familiale" className={`border-slate-300 focus:border-[#2A456B]${errCls('situation_familiale')}`}>
                      <SelectValue placeholder={t('selectMaritalStatus', language)} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="celibataire">Célibataire</SelectItem>
                      <SelectItem value="marie">Marié(e)</SelectItem>
                      <SelectItem value="marie_separation">Marié(e) en séparation de biens</SelectItem>
                      <SelectItem value="pacs">Pacsé(e)</SelectItem>
                      <SelectItem value="concubinage">Concubinage</SelectItem>
                      <SelectItem value="divorce">Divorcé(e)</SelectItem>
                      <SelectItem value="veuf">Veuf(ve)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nombre_enfants" className="text-slate-700 font-medium">Nombre d'enfants</Label>
                  <Input
                    id="nombre_enfants"
                    type="number"
                    min="0"
                    max="20"
                    value={formData.nombre_enfants === 0 ? '' : formData.nombre_enfants}
                    onFocus={() => handleNumericFocus('nombre_enfants')}
                    onBlur={(e) => handleNumericBlur('nombre_enfants', e.target.value)}
                    onChange={(e) => handleInputChange('nombre_enfants', e.target.value)}
                    className="border-slate-300 focus:border-[#2A456B]"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="type_achat" className="text-slate-700 font-medium">Vous souhaitez acheter</Label>
                <Select value={formData.type_achat} onValueChange={(value) => handleInputChange('type_achat', value)}>
                  <SelectTrigger data-testid="select-type-achat" className="border-slate-300 focus:border-[#2A456B]">
                    <SelectValue placeholder="Choisissez une option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seul">Seul</SelectItem>
                    <SelectItem value="deux">À deux</SelectItem>
                    <SelectItem value="sci">En SCI</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(['marie', 'marie_separation', 'pacs', 'concubinage'].includes(formData.situation_familiale) || formData.type_achat === 'deux') && (
                <div className="bg-[#FAF6EE] border border-[#DFCE9F] p-4 rounded-lg space-y-4" data-testid="conjoint-section">
                  <Label className="text-slate-700 font-medium text-base">Informations du conjoint</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Prénom du conjoint</Label>
                      <Input
                        value={formData.conjoint_prenom}
                        onChange={(e) => handleInputChange('conjoint_prenom', e.target.value)}
                        data-testid="input-conjoint-prenom"
                        className="border-slate-300 focus:border-[#C2A36B]"
                        placeholder="Prénom"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Nom du conjoint</Label>
                      <Input
                        value={formData.conjoint_nom}
                        onChange={(e) => handleInputChange('conjoint_nom', e.target.value)}
                        data-testid="input-conjoint-nom"
                        className="border-slate-300 focus:border-[#C2A36B]"
                        placeholder="Nom"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Téléphone du conjoint</Label>
                      <Input
                        type="tel"
                        value={formData.conjoint_telephone}
                        onChange={(e) => handleInputChange('conjoint_telephone', e.target.value)}
                        data-testid="input-conjoint-telephone"
                        className="border-slate-300 focus:border-[#C2A36B]"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Email du conjoint</Label>
                      <Input
                        type="email"
                        value={formData.conjoint_email}
                        onChange={(e) => handleInputChange('conjoint_email', e.target.value)}
                        data-testid="input-conjoint-email"
                        className="border-slate-300 focus:border-[#C2A36B]"
                        placeholder="email@exemple.com"
                      />
                    </div>
                  </div>
                </div>
              )}

              
              <div className="space-y-2">
                <Label htmlFor="loyer_actuel" className="text-slate-700 font-medium flex items-center">
                  <Euro className="h-4 w-4 mr-1" />
                  {t('currentRentLabel', language)}
                </Label>
                <Input
                  id="loyer_actuel"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.loyer_actuel === 0 ? '' : formData.loyer_actuel}
                  onFocus={() => handleNumericFocus('loyer_actuel')}
                  onBlur={(e) => handleNumericBlur('loyer_actuel', e.target.value)}
                  onChange={(e) => handleInputChange('loyer_actuel', e.target.value)}
                  className="border-slate-300 focus:border-[#2A456B]"
                  placeholder="0"
                />
              </div>
              
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">{t('netMonthlyIncome', language)}</h3>
                
                <div className="grid grid-cols-1 gap-6">
                  {/* Revenus Monsieur - Affiché conditionnellement */}
                  {shouldShowMonsieurFields() && (
                    <div className="space-y-4 border-b border-slate-200 pb-4">
                      <Label className="text-slate-700 font-medium text-base">{t('maleIncomeLabel', language)}</Label>
                      
                      {/* Nouveaux champs profession et lieu de travail */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label className="text-sm text-slate-600">{t('profession', language)} Monsieur *</Label>
                          <Input
                            value={formData.profession_monsieur}
                            onChange={(e) => handleInputChange('profession_monsieur', e.target.value)}
                            className="border-slate-300 focus:border-[#C2A36B]"
                            placeholder={t('professionPlaceholder', language)}
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-slate-600">{t('workplace', language)} (optionnel)</Label>
                          <AddressAutocomplete
                            value={formData.lieu_travail_monsieur}
                            onChange={(v) => handleInputChange('lieu_travail_monsieur', v)}
                            onSelect={(p, f) => setFormData(prev => ({
                              ...prev,
                              lieu_travail_monsieur: p.label || p.name || prev.lieu_travail_monsieur,
                              adresse_travail: p.label || p.name || prev.adresse_travail,
                              travail_lat: f?.geometry?.coordinates ? f.geometry.coordinates[1] : prev.travail_lat,
                              travail_lon: f?.geometry?.coordinates ? f.geometry.coordinates[0] : prev.travail_lon,
                            }))}
                            className="border-slate-300 focus:border-[#C2A36B]"
                            placeholder={t('workplacePlaceholder', language)}
                            data-testid="lieu-travail-monsieur"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm text-slate-600">{t('grossSalary', language)}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.salaire_brut_monsieur === 0 ? '' : formData.salaire_brut_monsieur}
                            onFocus={() => handleNumericFocus('salaire_brut_monsieur')}
                            onBlur={(e) => handleNumericBlur('salaire_brut_monsieur', e.target.value)}
                            onChange={(e) => handleInputChange('salaire_brut_monsieur', e.target.value)}
                            className="border-slate-300 focus:border-[#C2A36B]"
                            placeholder={t('grossSalaryPlaceholder', language)}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm text-slate-600">{t('netSalary', language)} *</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.salaire_net_monsieur === 0 ? '' : formData.salaire_net_monsieur}
                            onFocus={() => handleNumericFocus('salaire_net_monsieur')}
                            onBlur={(e) => handleNumericBlur('salaire_net_monsieur', e.target.value)}
                            onChange={(e) => handleInputChange('salaire_net_monsieur', e.target.value)}
                            data-testid="input-salaire-net-monsieur"
                            className={`border-slate-300 focus:border-[#C2A36B]${errCls('salaire_net_monsieur')}`}
                            placeholder={t('netSalaryPlaceholder', language)}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <Label className="text-sm text-slate-600">{t('status', language)} *</Label>
                        <RadioGroup
                          value={formData.statut_monsieur}
                          onValueChange={(value) => handleInputChange('statut_monsieur', value)}
                          data-testid="radio-statut-monsieur"
                          className={`flex flex-row space-x-4${isInvalid('statut_monsieur') ? ' border border-[#C2A36B] rounded-md p-2 bg-[#FAF6EE]' : ''}`}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="CDI" id="cdi_m" />
                            <Label htmlFor="cdi_m" className="text-sm">CDI</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="CDD" id="cdd_m" />
                            <Label htmlFor="cdd_m" className="text-sm">CDD</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Autres" id="autres_m" />
                            <Label htmlFor="autres_m" className="text-sm">Autres</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Sans emploi" id="sans_emploi_m" />
                            <Label htmlFor="sans_emploi_m" className="text-sm">Sans emploi</Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>
                  )}
                  
                  {/* Revenus Madame - Affiché conditionnellement */}
                  {shouldShowMadameFields() && (
                    <div className="space-y-4 border-b border-slate-200 pb-4">
                      <Label className="text-slate-700 font-medium text-base">{t('femaleIncomeLabel', language)}</Label>
                      
                      {/* Nouveaux champs profession et lieu de travail */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label className="text-sm text-slate-600">{t('profession', language)} Madame *</Label>
                          <Input
                            value={formData.profession_madame}
                            onChange={(e) => handleInputChange('profession_madame', e.target.value)}
                            className="border-slate-300 focus:border-[#C2A36B]"
                            placeholder={t('professionPlaceholder', language)}
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-slate-600">{t('workplace', language)} (optionnel)</Label>
                          <AddressAutocomplete
                            value={formData.lieu_travail_madame}
                            onChange={(v) => handleInputChange('lieu_travail_madame', v)}
                            onSelect={(p, f) => setFormData(prev => ({
                              ...prev,
                              lieu_travail_madame: p.label || p.name || prev.lieu_travail_madame,
                              adresse_travail: prev.adresse_travail || p.label || p.name || '',
                              travail_lat: f?.geometry?.coordinates ? f.geometry.coordinates[1] : prev.travail_lat,
                              travail_lon: f?.geometry?.coordinates ? f.geometry.coordinates[0] : prev.travail_lon,
                            }))}
                            className="border-slate-300 focus:border-[#C2A36B]"
                            placeholder={t('workplacePlaceholder', language)}
                            data-testid="lieu-travail-madame"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm text-slate-600">{t('grossSalary', language)}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.salaire_brut_madame === 0 ? '' : formData.salaire_brut_madame}
                            onFocus={() => handleNumericFocus('salaire_brut_madame')}
                            onBlur={(e) => handleNumericBlur('salaire_brut_madame', e.target.value)}
                            onChange={(e) => handleInputChange('salaire_brut_madame', e.target.value)}
                            className="border-slate-300 focus:border-[#C2A36B]"
                            placeholder={t('grossSalaryPlaceholder', language)}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm text-slate-600">{t('netSalary', language)} *</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.salaire_net_madame === 0 ? '' : formData.salaire_net_madame}
                            onFocus={() => handleNumericFocus('salaire_net_madame')}
                            onBlur={(e) => handleNumericBlur('salaire_net_madame', e.target.value)}
                            onChange={(e) => handleInputChange('salaire_net_madame', e.target.value)}
                            data-testid="input-salaire-net-madame"
                            className={`border-slate-300 focus:border-[#C2A36B]${errCls('salaire_net_madame')}`}
                            placeholder={t('netSalaryPlaceholder', language)}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <Label className="text-sm text-slate-600">{t('status', language)} *</Label>
                        <RadioGroup
                          value={formData.statut_madame}
                          onValueChange={(value) => handleInputChange('statut_madame', value)}
                          data-testid="radio-statut-madame"
                          className={`flex flex-row space-x-4${isInvalid('statut_madame') ? ' border border-[#C2A36B] rounded-md p-2 bg-[#FAF6EE]' : ''}`}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="CDI" id="cdi_f" />
                            <Label htmlFor="cdi_f" className="text-sm">CDI</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="CDD" id="cdd_f" />
                            <Label htmlFor="cdd_f" className="text-sm">CDD</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Autres" id="autres_f" />
                            <Label htmlFor="autres_f" className="text-sm">Autres</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Sans emploi" id="sans_emploi_f" />
                            <Label htmlFor="sans_emploi_f" className="text-sm">Sans emploi</Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>
                  )}
                  
                  {/* Revenus CAF */}
                  <div className="space-y-2">
                    <Label htmlFor="revenus_caf" className="text-slate-700 font-medium">{t('cafIncomeLabel', language)}</Label>
                    <Input
                      id="revenus_caf"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.revenus_caf === 0 ? '' : formData.revenus_caf}
                      onFocus={() => handleNumericFocus('revenus_caf')}
                      onBlur={(e) => handleNumericBlur('revenus_caf', e.target.value)}
                      onChange={(e) => handleInputChange('revenus_caf', e.target.value)}
                      className="border-slate-300 focus:border-[#C2A36B]"
                      placeholder={t('cafIncomePlaceholder', language)}
                    />
                  </div>

                  {/* Revenu fiscal de référence N-1 (facultatif) */}
                  <div className="space-y-2">
                    <Label htmlFor="revenu_fiscal_reference" className="text-slate-700 font-medium">
                      Revenu fiscal de référence de l'année n-1
                    </Label>
                    <p className="text-xs text-slate-500 -mt-1">
                      Disponible en 1ère page de votre avis d'imposition 2025 sur les revenus 2024 (facultatif)
                    </p>
                    <Input
                      id="revenu_fiscal_reference"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.revenu_fiscal_reference === 0 ? '' : formData.revenu_fiscal_reference}
                      onFocus={() => handleNumericFocus('revenu_fiscal_reference')}
                      onBlur={(e) => handleNumericBlur('revenu_fiscal_reference', e.target.value)}
                      onChange={(e) => handleInputChange('revenu_fiscal_reference', e.target.value)}
                      data-testid="input-revenu-fiscal-reference"
                      className="border-slate-300 focus:border-[#C2A36B]"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 3:
        return (
          <Card className="w-full">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center mb-4">
                <CreditCard className="h-8 w-8 text-[#8F7344] mr-2" />
                <CardTitle className="text-2xl text-slate-800">{t('financialSituation', language)}</CardTitle>
              </div>
              <CardDescription>{t('financialSituationDesc', language)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="banque_actuelle" className="text-slate-700 font-medium">{t('yourBanks', language)}</Label>
                <Input
                  id="banque_actuelle"
                  value={formData.banque_actuelle}
                  onChange={(e) => handleInputChange('banque_actuelle', e.target.value)}
                  className="border-slate-300 focus:border-[#2A456B]"
                  placeholder={t('yourBanksPlaceholder', language)}
                />
              </div>
              
              <div className="space-y-4">
                <Label htmlFor="credit_en_cours" className="text-slate-700 font-medium">{t('existingCreditQuestion', language)} *</Label>
                <Select value={formData.credit_en_cours} onValueChange={(value) => handleInputChange('credit_en_cours', value)}>
                  <SelectTrigger data-testid="select-credit-en-cours" className="border-slate-300 focus:border-[#2A456B]">
                    <SelectValue placeholder={t('selectAnswer', language)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non">{t('no', language)}</SelectItem>
                    <SelectItem value="oui">{t('yes', language)}</SelectItem>
                  </SelectContent>
                </Select>
                
                {formData.credit_en_cours === 'oui' && (
                  <div className="space-y-4 bg-[#EEF1F6] p-4 rounded-lg border border-[#AEBCCF]">
                    <h4 className="font-medium text-slate-800">{t('creditInfo', language)}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="organisme_credit" className="text-slate-700 font-medium">{t('creditOrganizationQuestion', language)} *</Label>
                      <Input
                        id="organisme_credit"
                        value={formData.organisme_credit}
                        onChange={(e) => handleInputChange('organisme_credit', e.target.value)}
                        className={`border-slate-300 focus:border-[#2A456B]${errCls('organisme_credit')}`}
                        placeholder={t('creditOrganizationPlaceholder', language)}
                        required={formData.credit_en_cours === 'oui'}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="mensualite_credit" className="text-slate-700 font-medium flex items-center">
                          <Euro className="h-4 w-4 mr-1" />
                          {t('monthlyPaymentQuestion', language)} *
                        </Label>
                        <Input
                          id="mensualite_credit"
                          type="number"
                          min="0"
                          step="10"
                          value={formData.mensualite_credit === 0 ? '' : formData.mensualite_credit}
                          onFocus={() => handleNumericFocus('mensualite_credit')}
                          onBlur={(e) => handleNumericBlur('mensualite_credit', e.target.value)}
                          onChange={(e) => handleInputChange('mensualite_credit', e.target.value)}
                          className={`border-slate-300 focus:border-[#2A456B]${errCls('mensualite_credit')}`}
                          placeholder={t('monthlyPaymentPlaceholder', language)}
                          required={formData.credit_en_cours === 'oui'}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="montant_restant_credit" className="text-slate-700 font-medium flex items-center">
                          <Euro className="h-4 w-4 mr-1" />
                          {t('remainingAmountQuestion', language)}
                        </Label>
                        <Input
                          id="montant_restant_credit"
                          type="number"
                          min="0"
                          step="1000"
                          value={formData.montant_restant_credit === 0 ? '' : formData.montant_restant_credit}
                          onFocus={() => handleNumericFocus('montant_restant_credit')}
                          onBlur={(e) => handleNumericBlur('montant_restant_credit', e.target.value)}
                          onChange={(e) => handleInputChange('montant_restant_credit', e.target.value)}
                          className="border-slate-300 focus:border-[#2A456B]"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );

      case 4:
        return (
          <Card className="w-full">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Building2 className="h-8 w-8 text-[#8F7344] mr-2" />
                <CardTitle className="text-2xl text-slate-800">{t('yourRealEstateProject', language)}</CardTitle>
              </div>
              <CardDescription>{t('describePropertyYouAreLookingFor', language)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="type_bien" className="text-slate-700 font-medium">{t('propertyTypeLabel', language)} *</Label>
                  <Select onValueChange={(value) => handleInputChange('type_bien', value)}>
                    <SelectTrigger data-testid="select-type-bien" className={`border-slate-300 focus:border-[#C2A36B]${errCls('type_bien')}`}>
                      <SelectValue placeholder={t('selectPropertyTypePlaceholder', language)} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appartement">{t('apartment', language)}</SelectItem>
                      <SelectItem value="maison">{t('house', language)}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="nombre_chambres" className="text-slate-700 font-medium">Nombre de pièces *</Label>
                  <Select onValueChange={(value) => handleInputChange('nombre_chambres', value)}>
                    <SelectTrigger data-testid="select-nombre-chambres" className={`border-slate-300 focus:border-[#C2A36B]${errCls('nombre_chambres')}`}>
                      <SelectValue placeholder="Sélectionnez le nombre de pièces" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 pièce</SelectItem>
                      <SelectItem value="2">2 pièces</SelectItem>
                      <SelectItem value="3">3 pièces</SelectItem>
                      <SelectItem value="4">4 pièces</SelectItem>
                      <SelectItem value="5+">5 pièces ou +</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="apport_projet" className="text-slate-700 font-medium flex items-center">
                    <Euro className="h-4 w-4 mr-1" />
                    Quel est l'apport disponible pour votre projet (€)
                  </Label>
                  <Input
                    id="apport_projet"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.apport_projet === 0 ? '' : formData.apport_projet}
                    onFocus={() => handleNumericFocus('apport_projet')}
                    onBlur={(e) => handleNumericBlur('apport_projet', e.target.value)}
                    onChange={(e) => handleInputChange('apport_projet', e.target.value)}
                    className="border-slate-300 focus:border-[#C2A36B]"
                    placeholder="Ex: 50000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mensualite_souhaitee" className="text-slate-700 font-medium flex items-center">
                    <Calculator className="h-4 w-4 mr-1" />
                    Mensualité de remboursement de prêt souhaitée (€)
                  </Label>
                  <Input
                    id="mensualite_souhaitee"
                    type="number"
                    min="0"
                    step="50"
                    value={formData.mensualite_souhaitee === 0 ? '' : formData.mensualite_souhaitee}
                    onFocus={() => handleNumericFocus('mensualite_souhaitee')}
                    onBlur={(e) => handleNumericBlur('mensualite_souhaitee', e.target.value)}
                    onChange={(e) => handleInputChange('mensualite_souhaitee', e.target.value)}
                    className="border-slate-300 focus:border-[#C2A36B]"
                    placeholder="Ex: 1500"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Où souhaiteriez-vous habiter (ville ou département) *</Label>
                  <AddressAutocomplete
                    id="ville_choix_1"
                    value={formData.ville_choix_1}
                    onChange={(v) => handleInputChange('ville_choix_1', v)}
                    onSelect={(p) => handleInputChange('ville_choix_1', p.name || p.city || p.label)}
                    type="municipality"
                    className={`border-slate-300 focus:border-[#C2A36B]${errCls('ville_choix_1')}`}
                    placeholder="Choix 1 (obligatoire) - Ex: Lyon, Rhône (69)"
                    data-testid="ville-choix-1"
                  />
                  <AddressAutocomplete
                    id="ville_choix_2"
                    value={formData.ville_choix_2}
                    onChange={(v) => handleInputChange('ville_choix_2', v)}
                    onSelect={(p) => handleInputChange('ville_choix_2', p.name || p.city || p.label)}
                    type="municipality"
                    className="border-slate-300 focus:border-[#C2A36B]"
                    placeholder="Choix 2 (optionnel)"
                    data-testid="ville-choix-2"
                  />
                  <AddressAutocomplete
                    id="ville_choix_3"
                    value={formData.ville_choix_3}
                    onChange={(v) => handleInputChange('ville_choix_3', v)}
                    onSelect={(p) => handleInputChange('ville_choix_3', p.name || p.city || p.label)}
                    type="municipality"
                    className="border-slate-300 focus:border-[#C2A36B]"
                    placeholder="Choix 3 (optionnel)"
                    data-testid="ville-choix-3"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-slate-700 font-medium">Les critères importants pour vous</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {CRITERES_OPTIONS.map((critere) => (
                    <label
                      key={critere}
                      className="flex items-center space-x-2 cursor-pointer rounded-md border border-slate-200 p-3 hover:bg-slate-50 transition-colors"
                      data-testid={`critere-${critere}`}
                    >
                      <Checkbox
                        checked={(formData.criteres || []).includes(critere)}
                        onCheckedChange={() => toggleCritere(critere)}
                      />
                      <span className="text-sm text-slate-700">{critere}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="duree_trajet" className="text-slate-700 font-medium">Quelle durée maximum accepteriez-vous pour vous rendre au travail ?</Label>
                <Select value={formData.duree_trajet} onValueChange={(value) => handleInputChange('duree_trajet', value)}>
                  <SelectTrigger data-testid="select-duree-trajet" className="border-slate-300 focus:border-[#C2A36B]">
                    <SelectValue placeholder="Sélectionnez une durée" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10 min">10 min</SelectItem>
                    <SelectItem value="20 min">20 min</SelectItem>
                    <SelectItem value="30 min">30 min</SelectItem>
                    <SelectItem value="45 min">45 min</SelectItem>
                    <SelectItem value="1h">1h</SelectItem>
                    <SelectItem value="1h15">1h15</SelectItem>
                    <SelectItem value="1h30">1h30</SelectItem>
                  </SelectContent>
                </Select>
                {formData.duree_trajet && (
                  <div className="space-y-2 step-anim">
                    <Label htmlFor="station_emploi" className="text-slate-700 font-medium">À quelle gare / station se trouve votre emploi ?</Label>
                    <GareAutocomplete
                      id="station_emploi"
                      value={formData.station_emploi}
                      onChange={(v) => setFormData(prev => ({ ...prev, station_emploi: v, gare_depart: '', gare_depart_lat: null, gare_depart_lon: null, gare_depart_lignes: null }))}
                      onSelect={(g) => setFormData(prev => ({
                        ...prev,
                        station_emploi: g.nom,
                        gare_depart: g.nom,
                        gare_depart_lat: g.lat,
                        gare_depart_lon: g.lon,
                        gare_depart_lignes: g.lignes || [],
                      }))}
                      className="border-slate-300 focus:border-[#C2A36B]"
                      placeholder="Ex: Châtelet, Gare de Lyon, La Défense..."
                      data-testid="station-emploi"
                    />
                    <p className="text-xs text-slate-400">Île-de-France : sélectionnez votre gare pour un calcul d'itinéraire précis.</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description_bien" className="text-slate-700 font-medium">Autres précisions (optionnel)</Label>
                <Textarea
                  id="description_bien"
                  value={formData.description_bien}
                  onChange={(e) => handleInputChange('description_bien', e.target.value)}
                  className="border-slate-300 focus:border-[#C2A36B] min-h-[120px]"
                  placeholder="Détaillez vos critères : présence de balcon/terrasse, parking, étage souhaité, exposition, prestations particulières, etc."
                />
              </div>
              
              <div className="bg-[#FAF6EE] border border-[#DFCE9F] rounded-lg p-4">
                <h3 className="font-semibold text-[#4D3D26] mb-2">Récapitulatif de votre demande</h3>
                <div className="text-sm text-[#6E5836] space-y-1">
                  <p><strong>Client :</strong> {formData.prenom} {formData.nom}</p>
                  <p><strong>Contact :</strong> {formData.telephone} - {formData.email}</p>
                  <p><strong>Situation :</strong> {formData.situation_familiale}</p>
                  <p><strong>Revenus totaux :</strong> {(formData.salaire_brut_monsieur + formData.salaire_net_monsieur + formData.salaire_brut_madame + formData.salaire_net_madame + formData.revenus_caf).toLocaleString()} €</p>
                  <p><strong>Apport :</strong> {formData.apport_projet.toLocaleString()} €</p>
                  <p><strong>Bien recherché :</strong> {formData.type_bien} - {formData.nombre_chambres} pièce(s)</p>
                  <p><strong>Villes/Départements :</strong> {formData.ville_choix_1 || 'Non précisé'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Sélecteur de langue en haut à droite */}
      <div className="absolute top-4 right-4 z-50">
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger className="w-[180px] bg-white shadow-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fr">🇫🇷 Français</SelectItem>
            <SelectItem value="en">🇬🇧 English</SelectItem>
            <SelectItem value="pt">🇵🇹 Português</SelectItem>
            <SelectItem value="es">🇪🇸 Español</SelectItem>
            <SelectItem value="it">🇮🇹 Italiano</SelectItem>
            <SelectItem value="ro">🇷🇴 Română</SelectItem>
            <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="container mx-auto px-4 py-8">
        {/* Header avec Logo Lovelia */}
        <div className="text-center mb-12">
          <a href="https://www.lovelia-immo.fr" target="_blank" rel="noopener noreferrer" data-testid="header-logo-link" className="inline-block transition-transform duration-300 hover:scale-105">
            <Logo size="large" className="mx-auto mb-6" />
          </a>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0E1E34] tracking-tight">Lovelia — Votre projet immobilier neuf</h1>
          <p className="text-sm text-[#C2A36B] font-semibold tracking-wide mt-2" data-testid="brand-tagline">Votre projet neuf, de la sélection à la remise des clés.</p>
          <p className="text-base md:text-lg text-[#142843]/80 max-w-2xl mx-auto mt-3 leading-relaxed">
            Quelques minutes pour nous aider à trouver le logement qui vous correspond.
          </p>
          <p className="text-xs text-[#4C6388] mt-3">
            (LOI n° 2018-493 du 20 juin 2018 relative à la protection des données personnelles)
          </p>
        </div>

        {/* Bannière de reprise du brouillon */}
        {showDraftBanner && (
          <div data-testid="draft-resume-banner" className="max-w-2xl mx-auto mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-800">{t('draftBannerText', language)}</p>
            <div className="flex gap-2 shrink-0">
              <Button type="button" size="sm" variant="outline" data-testid="draft-resume-button" onClick={() => setShowDraftBanner(false)} className="border-amber-300 text-amber-800 hover:bg-amber-100">{t('resume', language)}</Button>
              <Button type="button" size="sm" variant="ghost" data-testid="draft-startover-button" onClick={discardDraft} className="text-slate-500 hover:text-[#8F7344]">{t('startOver', language)}</Button>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">{t('stepProgress', language)} {currentStep} {t('of', language)} {totalSteps}</span>
            <span className="text-sm font-medium text-slate-600">{Math.round((currentStep / totalSteps) * 100)}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-[#C2A36B] to-[#B09259] h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            ></div>
          </div>
          {remainingCount(currentStep) > 0 ? (
            <p data-testid="remaining-fields-count" className="text-xs text-center text-[#8F7344] mt-2 font-medium">
              {remainingCount(currentStep)} {t('fieldsRemaining', language)}
            </p>
          ) : (
            <p data-testid="remaining-fields-count" className="text-xs text-center text-[#8F7344] mt-2 font-medium">
              {t('allFieldsCompleted', language)}
            </p>
          )}
          {currentStep === totalSteps ? (
            <p className="text-xs text-center text-slate-500 mt-1">{t('almostDone', language)}</p>
          ) : currentStep <= 2 ? (
            <p className="text-xs text-center text-slate-400 mt-1">{t('quickMinutes', language)}</p>
          ) : null}
        </div>

        {/* Bandeau conseiller attribué (dès l'étape 1) */}
        {commercialInfo && commercialInfo.name && (
          <div data-testid="advisor-banner" className="max-w-4xl mx-auto mb-4">
            <div className="flex flex-col items-center gap-1.5 rounded-xl border border-[#DFCE9F] bg-[#FAF6EE] px-4 py-2.5 text-sm text-[#0E1E34]">
              {commercialInfo.apporteur_name && (
                <div data-testid="referrer-line" className="flex items-center justify-center gap-2">
                  <Users className="w-4 h-4 text-[#8F7344] shrink-0" />
                  <span className="text-[#8F7344] font-medium">{t('recommendedBy', language)}</span>
                  <span className="font-semibold">{commercialInfo.apporteur_name}</span>
                </div>
              )}
              <div className="flex items-center justify-center gap-2">
                <UserCheck className="w-4 h-4 text-[#8F7344] shrink-0" />
                <span className="text-[#8F7344] font-medium">{t('yourAdvisor', language)}</span>
                <span className="font-semibold">{commercialInfo.name}</span>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit}>
            <div key={currentStep} className="step-anim">
              {renderStep()}
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-8">
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                data-testid="prev-step-button"
                className="px-6 py-2 border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                {t('previous', language)}
              </Button>
              
              {currentStep < totalSteps ? (
                <Button
                  key="btn-next"
                  type="button"
                  onClick={nextStep}
                  data-testid="next-step-button"
                  className="px-8 py-2 bg-[#0E1E34] hover:bg-[#142843] text-white"
                >
                  {t('next', language)}
                </Button>
              ) : (
                <Button
                  key="btn-submit"
                  type="submit"
                  disabled={isLoading}
                  data-testid="submit-form-button"
                  className="px-8 py-2 bg-[#0E1E34] hover:bg-[#142843] text-white"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t('sending', language)}</span>
                  ) : t('submit', language)}
                </Button>
              )}
            </div>
          </form>
        </div>

        {/* Récapitulatif avant envoi */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent data-testid="share-link-dialog">
            <DialogHeader>
              <DialogTitle>Ce questionnaire est-il pour vous ?</DialogTitle>
              <DialogDescription>
                Nous avons détecté une adresse email différente. Ce questionnaire est-il pour vous, ou souhaitez-vous le remplir pour un proche ? Pour préserver chaque dossier, un proche doit démarrer un questionnaire vierge.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                data-testid="share-friend-button"
                onClick={resetForFriend}
                className="border-slate-300"
              >
                C'est pour un proche (nouveau questionnaire)
              </Button>
              <Button
                data-testid="share-personal-button"
                onClick={() => {
                  const em = (formData.email || '').trim().toLowerCase();
                  const ph = (formData.telephone || '').trim();
                  regenerateSession();
                  sessionStorage.setItem('hp_bound_email', em);
                  sessionStorage.setItem('hp_bound_phone', ph);
                  setShowShareDialog(false);
                  advanceStep();
                }}
                className="bg-[#0E1E34] hover:bg-[#142843] text-white"
              >
                C'est pour moi, continuer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showRecap} onOpenChange={setShowRecap}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="recap-dialog">
            <DialogHeader>
              <DialogTitle className="text-slate-800">{t('reviewTitle', language)}</DialogTitle>
              <DialogDescription>{t('reviewDesc', language)}</DialogDescription>
            </DialogHeader>
            <div className="divide-y divide-slate-100">
              {buildRecap().map(([label, value], i) => (
                <div key={i} className="flex justify-between gap-4 py-2 text-sm">
                  <span className="text-slate-500">{label}</span>
                  <span className="text-slate-800 font-medium text-right break-words">{String(value)}</span>
                </div>
              ))}
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowRecap(false)}
                data-testid="recap-edit-button"
                className="border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                {t('edit', language)}
              </Button>
              <Button
                type="button"
                onClick={confirmAndSubmit}
                disabled={isLoading}
                data-testid="recap-confirm-button"
                className="bg-[#0E1E34] hover:bg-[#142843] text-white"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t('sending', language)}</span>
                ) : t('confirmSend', language)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Footer */}
        <div className="text-center mt-16 pt-8 border-t border-slate-200">
          <Logo size="small" className="mx-auto mb-4" />
          
          {/* Informations légales de l'entreprise */}
          <div className="max-w-4xl mx-auto mb-6 p-6 bg-slate-50 rounded-lg border border-slate-200">
            <h3 className="text-lg font-semibold text-[#0E1E34] mb-4">LOVELIA — IMMOBILIER NEUF</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600">
              <div className="space-y-1">
                <p><strong>Adresse :</strong></p>
                <p>1 place Paul Verlaine</p>
                <p>92100 BOULOGNE BILLANCOURT</p>
              </div>
              
              <div className="space-y-1">
                <p><strong>Contact :</strong></p>
                <p>Votre conseiller : {repContact.name}</p>
                <p>Téléphone : {repContact.phone}</p>
                <p>Email : <a href={`mailto:${repContact.email}`} className="text-[#8F7344] hover:underline">{repContact.email}</a></p>
                <p>Site : <a href="https://www.lovelia-immo.fr" target="_blank" rel="noopener noreferrer" className="text-[#8F7344] hover:underline">www.lovelia-immo.fr</a></p>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-slate-300 text-xs text-slate-500 space-y-1">
              <p><strong>Siret :</strong> 750 089 732 - R.C.S. Nanterre</p>
              <p><strong>Transaction sur immeubles et fonds de commerces</strong></p>
              <p>Carte professionnelle n° : CPI92012025000000108 délivrée par la CCI de Paris Île-de-France</p>
            </div>
          </div>
          
          <p className="text-slate-500">
            © 2026 Lovelia — Immobilier neuf
          </p>
          <p className="text-xs text-[#C2A36B] font-medium tracking-wide mt-1">Votre projet neuf, de la sélection à la remise des clés.</p>
          
          {/* Accès admin discret - invisible pour les clients */}
          <div className="mt-2">
            <a 
              href="/admin" 
              className="text-xs text-slate-300 hover:text-slate-400 transition-colors"
              style={{ fontSize: '10px', opacity: 0.3 }}
            >
              •
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<EstimationForm />} />
        <Route path="/reprendre" element={<EstimationForm />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
      <Toaster richColors position="top-right" />
    </BrowserRouter>
  );
}

export default App;